import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDictionary, Dictionary, DictionaryEntry } from '../firebase';
// Material UI imports
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Divider,
  IconButton,
  Paper,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Translate as TranslateIcon, 
  School as SchoolIcon, 
  FormatQuote as FormatQuoteIcon 
} from '@mui/icons-material';

const DictionaryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [dictionary, setDictionary] = useState<Dictionary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchDictionary = async () => {
      if (!id || !currentUser) return;
      
      try {
        setLoading(true);
        const result = await getDictionary(id);
        
        if (result.error) {
          setError(result.error);
        } else if (result.dictionary) {
          // Verify that the dictionary belongs to the current user
          if (result.dictionary.userId === currentUser.uid) {
            setDictionary(result.dictionary);
          } else {
            setError('You do not have permission to view this dictionary');
          }
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDictionary();
  }, [id, currentUser]);
  
  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={2}>
          <Typography variant="h5" component="h1" gutterBottom>
            Ошибка
          </Typography>
          <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleBackToDashboard}
            startIcon={<ArrowBackIcon />}
          >
            Вернуться к списку словарей
          </Button>
        </Paper>
      </Container>
    );
  }
  
  if (!dictionary) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={2}>
          <Typography variant="h5" component="h1" gutterBottom>
            Словарь не найден
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleBackToDashboard}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Вернуться к списку словарей
          </Button>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar position="static" color="default" elevation={2}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="primary" 
            onClick={handleBackToDashboard} 
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, color: 'primary.main' }}>
            LinguaSub
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            background: 'linear-gradient(45deg, #f8f9fe 30%, #eef2ff 90%)',
            borderRadius: 2
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              {dictionary.image && (
                <Box 
                  component="img"
                  src={dictionary.image}
                  alt={dictionary.title}
                  sx={{
                    width: '100%',
                    maxWidth: 200,
                    height: 'auto',
                    borderRadius: 2,
                    boxShadow: 3,
                    display: 'block',
                    mx: { xs: 'auto', md: 0 }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h1" gutterBottom>
                {dictionary.title}
              </Typography>
              {dictionary.year && (
                <Typography variant="h6" gutterBottom color="text.secondary">
                  {dictionary.year}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TranslateIcon color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Язык перевода
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {dictionary.targetLanguage}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon color="secondary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Уровень сложности
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {dictionary.cefrLevel}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormatQuoteIcon color="info" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Стиль речи
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {dictionary.speechStyle}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        <Typography variant="h5" component="h2" gutterBottom sx={{ 
          position: 'relative',
          pb: 1.5,
          mb: 3,
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: 60,
            height: 4,
            bgcolor: 'primary.main',
            borderRadius: 2
          }
        }}>
          Словарь фраз и выражений
        </Typography>
        
        <Grid container spacing={3}>
          {dictionary.entries.map((entry: DictionaryEntry, index: number) => (
            <Grid item xs={12} key={index}>
              <Card sx={{ mb: 2 }} elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        component="div" 
                        gutterBottom 
                        sx={{ color: 'primary.main', fontWeight: 'bold' }}
                      >
                        {entry.phrase}
                      </Typography>
                      <Typography 
                        variant="subtitle1" 
                        color="secondary" 
                        sx={{ fontWeight: 'medium' }}
                      >
                        {entry.translation}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Определение:
                        </Typography>
                        <Typography variant="body2" paragraph sx={{ mb: 0 }}>
                          {entry.definition}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Контекст сцены:
                        </Typography>
                        <Typography variant="body2" paragraph sx={{ mb: 0, fontStyle: 'italic' }}>
                          {entry.scene_description}
                        </Typography>
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ display: 'flex', mt: 2, pt: 1, borderTop: '1px dashed', borderColor: 'grey.200' }}>
                      <Chip 
                        label={dictionary.cefrLevel} 
                        size="small" 
                        color="secondary" 
                        variant="outlined" 
                        sx={{ mr: 1 }}
                      />
                      <Chip 
                        label={dictionary.speechStyle} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleBackToDashboard}
            startIcon={<ArrowBackIcon />}
            size="large"
          >
            Назад к списку
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DictionaryDetail; 